import { z } from 'zod';
import { zodiacSignEnum } from '../../zodiac';
import { birthDateSchema, birthTimeSchema, birthPlaceSchema, birthTimezoneSchema } from '../birth-data';

const normalizedBirthDataDetailedSchema = z
  .object({
    date: birthDateSchema,
    time: birthTimeSchema,
    timezone: birthTimezoneSchema,
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    place: birthPlaceSchema.optional(),
  })
  .optional()
  .nullable();

export const completeOnboardingInputSchema = z.object({
  name: z.string().trim().min(1).max(128),
  sign: zodiacSignEnum,
  dob: birthDateSchema,
  birthPlace: birthPlaceSchema,
  birthTime: birthTimeSchema,
  birthPlaceTimezone: birthTimezoneSchema.nullable().optional(),
  normalizedBirthData: normalizedBirthDataDetailedSchema,
  coreConcern: z.enum(['Love', 'Money', 'Trust', 'Direction']).optional().nullable(),
  clarityWindow: z.enum(['7d', '30d', 'crisis']).optional().nullable(),
  personContext: z
    .enum(['partner', 'crush', 'ex', 'self', 'friend', 'family', 'colleague'])
    .optional()
    .nullable(),
  primaryFear: z
    .enum([
      'betrayal',
      'losing_money',
      'stuck',
      'miss_chance',
      'debt',
      'bad_luck',
      'stagnation',
      'lost',
      'overwhelmed',
      'numb',
    ])
    .optional()
    .nullable(),
});

export type CompleteOnboardingInput = z.infer<typeof completeOnboardingInputSchema>;

export const updateProfileInputSchema = z.object({
  name: z.string().trim().min(1).max(128).optional(),
  sign: zodiacSignEnum.optional(),
  dob: birthDateSchema.nullable().optional(),
  birthPlace: birthPlaceSchema.nullable().optional(),
  birthTime: birthTimeSchema.nullable().optional(),
  birthPlaceTimezone: birthTimezoneSchema.nullable().optional(),
});

export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;

export const addCosmicDustInputSchema = z.object({
  amount: z.number(),
  source: z.string(),
});

export type AddCosmicDustInput = z.infer<typeof addCosmicDustInputSchema>;

export const cacheInterpretationInputSchema = z.object({
  key: z.string(),
  interpretation: z.string(),
});

export type CacheInterpretationInput = z.infer<typeof cacheInterpretationInputSchema>;

export const unlockAchievementInputSchema = z.object({
  achievementId: z.string(),
});

export type UnlockAchievementInput = z.infer<typeof unlockAchievementInputSchema>;

export const natalChartInputSchema = z.object({
  dob: birthDateSchema,
  birthTime: birthTimeSchema,
  birthPlace: birthPlaceSchema,
  birthPlaceTimezone: birthTimezoneSchema.nullable(),
});

export type NatalChartInput = z.infer<typeof natalChartInputSchema>;

export const debugNatalChartInputSchema = z.object({
  birthDate: z.string(),
  birthTime: z.string(),
  birthPlace: z.string(),
  referenceData: z
    .object({
      expectedSunSign: z.string(),
      expectedMoonSign: z.string(),
      expectedAscendantSign: z.string(),
    })
    .optional(),
});

export type DebugNatalChartInput = z.infer<typeof debugNatalChartInputSchema>;
